import { useState } from 'react';

import { Input } from '@components';
import CalendarV2 from '@components/calendar/CalendarV2';
import {
  META_ACCESS_TOKEN_VARIANT,
  MIN_SELECTABLE_DATE
} from '@features/lead-management/constants/leadSettings';
import ModalV2 from '@layouts/modal/Modal.v2';

import MetaAccessTokenBanner from './MetaAccessTokenBanner';

interface SetupMetaIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (accessToken: string, date: Date) => void;
}

const SetupMetaIntegrationModal = ({
  isOpen,
  onClose,
  onConfirm
}: SetupMetaIntegrationModalProps) => {
  const [accessToken, setAccessToken] = useState('');
  const [syncDate, setSyncDate] = useState<Date | null>(new Date());

  const handleApply = () => {
    if (accessToken.trim() && syncDate) {
      onConfirm(accessToken, syncDate);
      onClose();
      setAccessToken('');
    }
  };

  if (!isOpen) return null;

  return (
    <ModalV2
      title="Meta integration"
      onClose={onClose}
      showCloseButton
      primaryButtonLabel="Sync"
      secondaryButtonLabel="Cancel"
      primaryButtonClick={handleApply}
      secondaryButtonClick={onClose}
      primaryButtonDisabled={!accessToken.trim() || !syncDate}
      isClickOutsideRequired
      className="h-[86%] !w-[462px] !rounded-2xl"
      primaryButtonStyle="!w-[100px] !h-[50px]"
      secondaryButtonStyle="!w-[100px] !h-[50px]"
    >
      <div className="flex flex-col gap-[12px]">
        <div className="space-y-2">
          <label className="text-sm font-semibold tracking-[-0.2px] text-greyText1">
            Access token<span className="text-error"> *</span>
          </label>
          <Input
            type="text"
            value={accessToken}
            handleInputChange={setAccessToken}
            placeholder="Enter access token"
            className="w-full"
            inputStyle="h-12.5 w-full rounded-lg border border-grey30 px-4 py-2.5 text-base font-normal leading-none tracking-normal placeholder:text-cofeeNeutral6 focus:border-cofeePrimary6 focus:outline-none"
          />
          <MetaAccessTokenBanner
            variant={META_ACCESS_TOKEN_VARIANT.ACCORDION}
            bannerClassName="!px-3"
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-sm font-semibold tracking-[-0.2px] text-greyText1">
            Sync leads from
          </div>
          <p className="text-sm text-grey">
            Only leads captured on or after the selected date will be synced
            from Meta.
          </p>
          <CalendarV2
            date={syncDate}
            onChange={(date) => setSyncDate(date)}
            minDate={MIN_SELECTABLE_DATE}
            maxDate={new Date()}
          />
        </div>
      </div>
    </ModalV2>
  );
};

export default SetupMetaIntegrationModal;
